import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.digital_twin.pid');
import { OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import Chart from 'chart.js/auto';
import { Service } from "src/libs/portal/season/service";

declare let wiz: any;

const PROJECT_WORKFLOW_ID_KEY = 'actuator:lastProjectId';

@Component({
    standalone: false,
    selector: 'wiz-page-digital_twin-pid',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.digital_twin.pid/view.scss */
:host {
  display: block;
  height: 100%;
}

.twin-shell {
  background: linear-gradient(135deg, var(--twin-bg-start), var(--twin-bg-mid), var(--twin-bg-end));
  color: var(--twin-text);
  transition: background-color 180ms ease, color 180ms ease;
  --twin-bg-start: #f8fafc;
  --twin-bg-mid: #ffffff;
  --twin-bg-end: #e2e8f0;
  --twin-text: #0f172a;
  --twin-muted: #64748b;
  --twin-border: rgba(148, 163, 184, 0.36);
  --twin-card: rgba(255, 255, 255, 0.88);
}

:host-context(.dark) .twin-shell,
.twin-dark {
  --twin-bg-start: #020617;
  --twin-bg-mid: #0f172a;
  --twin-bg-end: #18181b;
  --twin-text: #f8fafc;
  --twin-muted: #94a3b8;
  --twin-border: rgba(255, 255, 255, 0.1);
  --twin-card: rgba(15, 23, 42, 0.62);
}

.twin-light [class*="border-white/"] {
  border-color: var(--twin-border) !important;
}

.twin-light [class*=bg-slate-950],
.twin-light [class*="bg-white/5"] {
  background-color: var(--twin-card) !important;
}

.twin-light [class*=bg-slate-800] {
  background-color: #e2e8f0 !important;
}

.twin-light .text-white,
.twin-light .text-slate-100,
.twin-light .text-slate-200 {
  color: var(--twin-text) !important;
}

.twin-light .text-slate-300,
.twin-light .text-slate-400,
.twin-light .text-slate-500 {
  color: var(--twin-muted) !important;
}

.twin-light .text-cyan-100,
.twin-light .text-cyan-200,
.twin-light .text-cyan-300,
.twin-light .text-cyan-400 {
  color: #0e7490 !important;
}

.twin-light .text-emerald-100,
.twin-light .text-emerald-200,
.twin-light .text-emerald-300 {
  color: #047857 !important;
}

.twin-light .text-amber-100,
.twin-light .text-amber-200,
.twin-light .text-amber-300 {
  color: #b45309 !important;
}

.twin-light .text-orange-100,
.twin-light .text-orange-200,
.twin-light .text-orange-300 {
  color: #c2410c !important;
}

.twin-light tbody tr:hover {
  background-color: rgba(241, 245, 249, 0.72) !important;
}`],
})
export class PageDigital_twinPidComponent implements OnInit, OnDestroy {
    constructor(@Inject( Service) public service: Service) {}

    public theme: 'light' | 'dark' = 'light';
    public projectId: string = '';
    private get isDark(): boolean { return this.theme === 'dark'; }

    public pidComparison: any[] = [];
    public pidComparisonMeta: any = {};

    @ViewChild('pidChart') pidChart?: ElementRef<HTMLCanvasElement>;
    public pidKp: number = 1.25;
    public pidKi: number = 0.48;
    public pidKd: number = 0.12;
    public pidTarget: number = 80;
    public pidResult: any = null;
    public isPidRunning: boolean = false;
    private pidChartInstance: any = null;

    public async ngOnInit() {
        await this.service.init();
        this.projectId = this.routeProjectId();
        this.rememberProjectId(this.projectId);
        this.syncGlobalTheme();
        await this.loadPidDefaults();
        await this.loadPidComparison();
        await this.service.render();
    }

    ngOnDestroy() {
        if (this.pidChartInstance) { this.pidChartInstance.destroy(); this.pidChartInstance = null; }
    }

    private syncGlobalTheme() {
        try {
            const saved = localStorage.getItem('appTheme');
            this.theme = saved === 'dark' ? 'dark' : 'light';
        } catch (e) { this.theme = 'light'; }
    }

    private routeProjectId(): string {
        const params = new URLSearchParams(location.search);
        const queryId = params.get('project_id') || '';
        if (queryId) return queryId;
        try {
            return localStorage.getItem(PROJECT_WORKFLOW_ID_KEY) || '';
        } catch (e) {
            return '';
        }
    }

    private rememberProjectId(id: string) {
        if (!id) return;
        try {
            localStorage.setItem(PROJECT_WORKFLOW_ID_KEY, id);
        } catch (e) {}
    }

    public digitalTwinToolUrl(path: string): string {
        return this.projectId ? `${path}?project_id=${encodeURIComponent(this.projectId)}` : path;
    }

    public projectDigitalTwinUrl(): string {
        return this.projectId ? `/projects/${encodeURIComponent(this.projectId)}/digital_twin` : '/projects';
    }

    public goDigitalTwinTool(path: string) {
        this.service.href(this.digitalTwinToolUrl(path));
    }

    public goProjectDigitalTwin() {
        this.service.href(this.projectDigitalTwinUrl());
    }

    private async loadPidComparison() {
        const params = this.projectId ? { project_id: this.projectId } : {};
        const { code, data } = await wiz.call("pid_comparison", params);
        if (code === 200) {
            if (Array.isArray(data)) {
                this.pidComparison = data;
                this.pidComparisonMeta = {};
            } else {
                this.pidComparison = data?.rows || [];
                this.pidComparisonMeta = data?.meta || {};
            }
        }
    }

    private async loadPidDefaults() {
        const params = this.projectId ? { project_id: this.projectId } : {};
        const { code, data } = await wiz.call("pid_defaults", params);
        if (code !== 200 || !data) return;
        this.pidKp = data.kp ?? this.pidKp;
        this.pidKi = data.ki ?? this.pidKi;
        this.pidKd = data.kd ?? this.pidKd;
        this.pidTarget = data.target ?? this.pidTarget;
    }

    public getDeviationColor(deviation: number): string {
        if (deviation == null) return this.theme === 'dark' ? 'text-slate-500' : 'text-slate-400';
        if (deviation > 20) return this.theme === 'dark' ? 'text-orange-200' : 'text-orange-700';
        if (deviation > 10) return this.theme === 'dark' ? 'text-amber-200' : 'text-amber-700';
        return this.theme === 'dark' ? 'text-cyan-200' : 'text-cyan-700';
    }

    public async runPidSimulation() {
        this.isPidRunning = true;
        await this.service.render();
        const { code, data } = await wiz.call("pid_simulate", {
            project_id: this.projectId,
            kp: this.pidKp, ki: this.pidKi, kd: this.pidKd, target: this.pidTarget
        });
        this.isPidRunning = false;
        if (code === 200) {
            this.pidResult = data;
            await this.service.render();
            setTimeout(() => this.renderPidChart(), 50);
        } else {
            await this.service.render();
        }
    }

    private renderPidChart() {
        if (this.pidChartInstance) { this.pidChartInstance.destroy(); this.pidChartInstance = null; }
        const el = this.pidChart?.nativeElement;
        if (!el || !this.pidResult?.response?.length) return;

        const resp = this.pidResult.response;

        this.pidChartInstance = new Chart(el, {
            type: 'line',
            data: {
                labels: resp.map((r: any) => r.time),
                datasets: [
                    {
                        label: '응답',
                        data: resp.map((r: any) => r.value),
                        borderColor: '#22d3ee',
                        fill: false,
                        tension: 0.3,
                        pointRadius: 0,
                        borderWidth: 2,
                    },
                    {
                        label: '목표',
                        data: resp.map((r: any) => r.target),
                        borderColor: '#f97316',
                        borderDash: [6, 3],
                        fill: false,
                        tension: 0,
                        pointRadius: 0,
                        borderWidth: 1.5,
                    },
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: true, labels: { color: '#64748b', boxWidth: 10, font: { size: 10 } } } },
                scales: {
                    x: { title: { display: true, text: 'Time (ms)', color: '#94a3b8', font: { size: 9 } }, ticks: { color: '#94a3b8', font: { size: 9 } }, grid: { color: 'rgba(148,163,184,0.12)' } },
                    y: { title: { display: true, text: 'Angle (°)', color: '#94a3b8', font: { size: 9 } }, ticks: { color: '#94a3b8', font: { size: 9 } }, grid: { color: 'rgba(148,163,184,0.12)' } },
                }
            }
        });
    }
}

export default PageDigital_twinPidComponent;